import { useCallback, useLayoutEffect } from "react";
import { useStudentFilterContext } from "../../components/features/NewStudentFilter/useStudentFilterContext";
import { toQueryObject } from "../../helpers/FiltersHelper";
import { useOnLocationChange } from "../../helpers/RouterHelper";
import { AnalyticsTermType } from "../../interfaces/AnalyticsTableInterface";
import { DateRangeFilterQueryInterface } from "../../interfaces/DateRangeFilterInterface";
import {
  FiltersQueryInterface,
  OrderDirFilterType,
} from "../../interfaces/FiltersInterface";
import { useFilter } from "../useFilter";
import { usePushHistory } from "../usePushHistory";

export type AnalyticsFilter = {
  term: AnalyticsTermType;
  startDate: Date;
  endDate: Date;
  order: string | null;
  orderDir: "asc" | "desc" | null;
  enabledRequest: boolean;
};

export const useAnalyticsFilter = ({
  withStudentFilter,
}: {
  withStudentFilter: boolean;
}) => {
  const context = useStudentFilterContext();
  const { filter, enabledRequest } = useFilter();
  const { pushHistory } = usePushHistory<{
    initial_date?: string;
    end_date?: string;
    term?: string;
    order?: string;
    order_dir?: string;
  }>();

  const analyticsFilter: AnalyticsFilter = {
    term: filter.term,
    startDate: filter.dateRangeFilter.startDate,
    endDate: filter.dateRangeFilter.endDate,
    order: filter.order ?? null,
    orderDir: filter.orderDir || null, // 空文字の場合もnullにするために||を使っている
    enabledRequest,
  };
  const studentFilter = context.currentFilter;

  // 遷移時にURLパラメータからフィルターを復元する
  const updateStudentFilterByQuery = useCallback(() => {
    // 生徒フィルターがあるときStudentFilterで行う処理のためスキップ
    if (withStudentFilter) {
      return;
    }

    context.setIsInitialized(true);
    context.updateStudentFilterByQuery();
  }, [
    context.setIsInitialized,
    context.updateStudentFilterByQuery,
    withStudentFilter,
  ]);

  useLayoutEffect(() => {
    // filtersQueryが存在する = 別ページでフィルターを設定したことがある
    // 別ページのフィルター設定は引き継ぎたいが、その状態でqueryから状態を作ると上書きしてしまうため、スキップする
    if (!context.filtersQuery.current) {
      updateStudentFilterByQuery();
    }
  }, [updateStudentFilterByQuery]);

  useOnLocationChange(() => {
    updateStudentFilterByQuery();
  });

  const pushFilterHistory = (obj: FiltersQueryInterface) => {
    const mergedParams = toQueryObject({
      ...filter,
      ...(studentFilter ? { tagFilter: studentFilter } : {}),
      ...obj,
    });

    pushHistory({
      initial_date: mergedParams.initial_date,
      end_date: mergedParams.end_date,
      term: mergedParams.term,
      order: mergedParams.order,
      order_dir: mergedParams.order_dir,
    });
  };

  const changeDates = (dateRangeFilter: DateRangeFilterQueryInterface) => {
    pushFilterHistory({ dateRangeFilter });
  };

  const changeTerm = (term: AnalyticsTermType) => {
    // termが変更されるときはdateの範囲をリセット
    pushFilterHistory({ term, dateRangeFilter: undefined });
  };

  const changeOrder = (
    order: string | null,
    initialOrderDir: OrderDirFilterType = "desc",
  ) => {
    if (analyticsFilter.order !== order) {
      pushFilterHistory({ order, orderDir: initialOrderDir });
    } else {
      const orderDir = analyticsFilter.orderDir === "desc" ? "asc" : "desc";
      pushFilterHistory({ order, orderDir });
    }
  };

  return {
    analyticsFilter,
    studentFilter,
    changeDates,
    changeTerm,
    changeOrder,
  };
};
